import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Grid,
    TextField,
    Typography,
    Alert,
    Snackbar,
    Paper,
    CircularProgress,
    Autocomplete,
    Card,
    CardContent,
    Chip
} from '@mui/material';
import MainCard from 'ui-component/cards/MainCard';
import { listCronogramaAnual } from 'services/cronogramaAnual';
import FilterListIcon from '@mui/icons-material/FilterList';
import { gridSpacing } from 'store/constant';
import { MENU_OPEN } from 'store/actions';
import { useDispatch, useSelector } from 'react-redux';

// Mapeamento de meses
const MESES = {
    'JAN': 'Janeiro',
    'FEV': 'Fevereiro',
    'MAR': 'Março',
    'ABR': 'Abril',
    'MAI': 'Maio',
    'JUN': 'Junho',
    'JUL': 'Julho',
    'AGO': 'Agosto',
    'SET': 'Setembro',
    'OUT': 'Outubro',
    'NOV': 'Novembro',
    'DEZ': 'Dezembro'
};

const MESES_KEYS = Object.keys(MESES);

const CronogramaAnualView = ({ ...others }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const idUnit = useSelector((state) => state.user.unit || '');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [cronogramas, setCronogramas] = useState([]);
    
    // Obter mês atual (JAN, FEV, MAR, etc)
    const getMesAtual = () => {
        const mesAtual = new Date().getMonth(); // 0-11
        return MESES_KEYS[mesAtual];
    };
    
    const [ano, setAno] = useState(new Date().getFullYear());
    const [mes, setMes] = useState(getMesAtual());

    useEffect(() => {
        const currentIndex = document.location.pathname
            .toString()
            .split('/')
            .findIndex((id) => id === 'cronograma-anual-view');
        if (currentIndex > -1) {
            dispatch({ type: MENU_OPEN, id: 'cronograma-anual-view' });
        }
    }, [dispatch]);

    const handleSearch = async () => {
        try {
            setLoading(true);
            setError('');
            setSuccess('');

            // Usar unidade do Redux
            const unidadeId = idUnit || null;
            const response = await listCronogramaAnual(ano, mes, unidadeId);

            if (response.data.success) {
                setCronogramas(response.data.data || []);
                setSuccess(`${response.data.data.length} unidade(s) encontrada(s).`);
                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError(response.data.error || 'Erro ao buscar cronograma.');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao buscar cronograma. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    // Buscar automaticamente quando o componente montar com ano e mês pré-preenchidos
    useEffect(() => {
        if (ano && mes) {
            handleSearch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Apenas no mount inicial

    // Função para obter número de dias no mês
    const getDaysInMonth = (mesKey, ano) => {
        const mesIndex = MESES_KEYS.indexOf(mesKey);
        if (mesIndex === -1) return 31;
        // Date usa 0-11 para meses, então mesIndex já está correto (JAN=0, FEV=1, etc)
        return new Date(ano, mesIndex + 1, 0).getDate();
    };

    // Função para renderizar calendário de uma unidade
    const renderCalendarioUnidade = (unidade) => {
        if (!mes || !unidade.meses[mes]) {
            return (
                <Typography variant="body2" color="text.secondary">
                    Nenhum cronograma para este mês.
                </Typography>
            );
        }

        const diasDoMes = getDaysInMonth(mes, ano);
        const diasComManutencao = unidade.meses[mes];
        const mesIndex = MESES_KEYS.indexOf(mes);
        
        // Obter o dia da semana do primeiro dia do mês (0 = domingo, 1 = segunda, etc)
        const primeiroDiaSemana = new Date(ano, mesIndex, 1).getDay();
        
        const dias = [];

        // Adicionar dias vazios antes do primeiro dia do mês
        for (let i = 0; i < primeiroDiaSemana; i++) {
            dias.push({ numero: null, equipamentos: [] });
        }

        // Criar array com todos os dias do mês
        for (let dia = 1; dia <= diasDoMes; dia++) {
            dias.push({
                numero: dia,
                equipamentos: diasComManutencao[dia] || []
            });
        }

        // Dividir em semanas (7 dias por semana)
        const semanas = [];
        for (let i = 0; i < dias.length; i += 7) {
            semanas.push(dias.slice(i, i + 7));
        }

        return (
            <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    {unidade.unidade_nome}
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 800 }}>
                        {/* Cabeçalho dos dias da semana */}
                        <Grid container spacing={1} sx={{ mb: 1 }}>
                            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((diaSemana, index) => (
                                <Grid item xs={12/7} key={index}>
                                    <Paper
                                        sx={{
                                            p: 1,
                                            textAlign: 'center',
                                            backgroundColor: theme.palette.primary.main,
                                            color: 'white',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {diaSemana}
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Dias do mês */}
                        {semanas.map((semana, semanaIndex) => (
                            <Grid container spacing={1} key={semanaIndex} sx={{ mb: 1 }}>
                                {semana.map((dia, diaIndex) => (
                                    <Grid item xs={12/7} key={diaIndex}>
                                        {dia.numero ? (
                                            <Card
                                                sx={{
                                                    minHeight: 120,
                                                    border: dia.equipamentos.length > 0 ? `2px solid ${theme.palette.success.main}` : '1px solid #e0e0e0',
                                                    backgroundColor: dia.equipamentos.length > 0 ? theme.palette.success.light : 'white'
                                                }}
                                            >
                                                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            mb: 0.5,
                                                            color: dia.equipamentos.length > 0 ? theme.palette.success.dark : 'text.primary'
                                                        }}
                                                    >
                                                        {dia.numero}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        {dia.equipamentos.map((equip, idx) => (
                                                            <Chip
                                                                key={idx}
                                                                label={`${equip.equipamento} (${equip.quantidade})`}
                                                                size="small"
                                                                sx={{
                                                                    fontSize: '0.7rem',
                                                                    height: 'auto',
                                                                    py: 0.5,
                                                                    '& .MuiChip-label': {
                                                                        whiteSpace: 'normal',
                                                                        textAlign: 'center'
                                                                    }
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        ) : (
                                            <Box sx={{ minHeight: 120 }} />
                                        )}
                                    </Grid>
                                ))}
                            </Grid>
                        ))}
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <Box>
            <Grid container spacing={gridSpacing}>
                {/* Filtros */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Ano"
                                    type="number"
                                    value={ano}
                                    onChange={(e) => setAno(parseInt(e.target.value) || new Date().getFullYear())}
                                    inputProps={{ min: 2020, max: 2100 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    options={MESES_KEYS}
                                    getOptionLabel={(option) => `${option} - ${MESES[option]}`}
                                    value={mes}
                                    onChange={(event, newValue) => {
                                        setMes(newValue);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Mês"
                                            placeholder="Selecione o mês..."
                                        />
                                    )}
                                    noOptionsText="Nenhum mês encontrado"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={<FilterListIcon />}
                                    onClick={handleSearch}
                                    disabled={loading || !ano || !mes}
                                >
                                    Buscar
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Mensagens de sucesso/erro */}
                {success && (
                    <Grid item xs={12}>
                        <Alert severity="success" onClose={() => setSuccess('')}>
                            {success}
                        </Alert>
                    </Grid>
                )}
                {error && (
                    <Grid item xs={12}>
                        <Alert severity="error" onClose={() => setError('')}>
                            {error}
                        </Alert>
                    </Grid>
                )}

                {/* Calendários */}
                {loading ? (
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    </Grid>
                ) : cronogramas.length > 0 ? (
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {cronogramas.map((unidade) => (
                                <Paper key={unidade.unidade_id} sx={{ p: 3 }}>
                                    {renderCalendarioUnidade(unidade)}
                                </Paper>
                            ))}
                        </Box>
                    </Grid>
                ) : ano && mes ? (
                    <Grid item xs={12}>
                        <Alert severity="info">
                            Nenhum cronograma encontrado para os filtros selecionados.
                        </Alert>
                    </Grid>
                ) : null}
            </Grid>
        </Box>
    );
};

export default CronogramaAnualView;
